import { create } from 'zustand';

/**
 * Session-only collapse state for the designer canvas.
 *
 * Collapsing a node hides its children on the canvas:
 *  - Agent nodes hide their tool nodes.
 *  - Container nodes (If, Switch, Foreach, Until) hide their child-action
 *    subtrees. Bare `Scope` is intentionally excluded in v1 — the workflow
 *    store's mutation path does not yet traverse Scope children, so flattening
 *    them onto the canvas would risk duplicating an edited child at the root.
 *
 * Hidden nodes are removed from the render set (`hidden: true`); canonical
 * positions are never mutated, so expanding restores the exact prior layout and
 * a page reload can never leave the canvas in a compacted/overlapping state.
 *
 * Keyed by canvas `node.id` (e.g. `action-ForEach_1`, `action-ForEach_1/Compose`,
 * `action-MyAgent`) — NOT by action name, which collides for identically-named
 * children nested in different scopes.
 *
 * IMPORTANT: This store is intentionally NOT persisted.
 */
interface NodeCollapseState {
  /** Canvas node ids that are currently collapsed. */
  collapsedNodes: Set<string>;

  /**
   * Agent ids explicitly expanded in monitoring / debug (read-only) mode.
   *
   * Monitoring defaults an agent to its *invoked-only* tool view (collapsed);
   * membership here opts a single agent into the full expanded tool view for
   * the current session. This is a separate axis from {@link collapsedNodes}
   * (which drives design-mode collapse) so toggling in one mode never leaks
   * into the other. Container scopes are read-only in monitoring and are not
   * tracked here.
   */
  monitoringExpandedNodes: Set<string>;

  /** Whether a node is collapsed. */
  isCollapsed: (id: string) => boolean;

  /** Toggle a node's collapse state. */
  toggle: (id: string) => void;

  /** Explicitly set a node's collapse state. */
  setCollapsed: (id: string, collapsed: boolean) => void;

  /** Expand a node (e.g. auto-expand when a child is added). */
  expand: (id: string) => void;

  /** Whether an agent is expanded in monitoring mode (default: collapsed). */
  isExpandedInMonitoring: (id: string) => boolean;

  /** Toggle an agent's monitoring expanded state. */
  toggleMonitoring: (id: string) => void;

  /** Explicitly set an agent's monitoring expanded state. */
  setMonitoringExpanded: (id: string, expanded: boolean) => void;

  /** Drop collapse entries whose ids are no longer on the canvas. */
  pruneMissing: (liveIds: Set<string>) => void;

  /** Clear all collapse state (e.g. when switching workflows). */
  clearAll: () => void;
}

export const useNodeCollapseStore = create<NodeCollapseState>((set, get) => ({
  collapsedNodes: new Set<string>(),
  monitoringExpandedNodes: new Set<string>(),

  isCollapsed: (id) => get().collapsedNodes.has(id),

  toggle: (id) =>
    set((state) => {
      const next = new Set(state.collapsedNodes);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { collapsedNodes: next };
    }),

  setCollapsed: (id, collapsed) =>
    set((state) => {
      if (state.collapsedNodes.has(id) === collapsed) return state;
      const next = new Set(state.collapsedNodes);
      if (collapsed) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { collapsedNodes: next };
    }),

  expand: (id) =>
    set((state) => {
      if (!state.collapsedNodes.has(id)) return state;
      const next = new Set(state.collapsedNodes);
      next.delete(id);
      return { collapsedNodes: next };
    }),

  isExpandedInMonitoring: (id) => get().monitoringExpandedNodes.has(id),

  toggleMonitoring: (id) =>
    set((state) => {
      const next = new Set(state.monitoringExpandedNodes);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { monitoringExpandedNodes: next };
    }),

  setMonitoringExpanded: (id, expanded) =>
    set((state) => {
      if (state.monitoringExpandedNodes.has(id) === expanded) return state;
      const next = new Set(state.monitoringExpandedNodes);
      if (expanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { monitoringExpandedNodes: next };
    }),

  pruneMissing: (liveIds) =>
    set((state) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of state.collapsedNodes) {
        if (liveIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      let monitoringChanged = false;
      const nextMonitoring = new Set<string>();
      for (const id of state.monitoringExpandedNodes) {
        if (liveIds.has(id)) {
          nextMonitoring.add(id);
        } else {
          monitoringChanged = true;
        }
      }
      if (!changed && !monitoringChanged) return state;
      return {
        ...(changed ? { collapsedNodes: next } : {}),
        ...(monitoringChanged ? { monitoringExpandedNodes: nextMonitoring } : {}),
      };
    }),

  clearAll: () =>
    set({
      collapsedNodes: new Set<string>(),
      monitoringExpandedNodes: new Set<string>(),
    }),
}));
